export type OpenAINativeRolloutStage = "off" | "canary10" | "canary25" | "canary50" | "on";

export interface OpenAINativeRolloutTelemetry {
  enabled: boolean;
  stage: OpenAINativeRolloutStage;
  bucket?: number;
  rolloutPercent?: number;
  source: "explicit" | "stage";
}

const OPENAI_NATIVE_ENABLED_KEY = "finster_openai_native_enabled";
const OPENAI_NATIVE_STAGE_KEY = "finster_openai_native_stage";
const OPENAI_NATIVE_BUCKET_KEY = "finster_openai_native_bucket";
const DEFAULT_STAGE: OpenAINativeRolloutStage = "on";

function getStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    return localStorage;
  } catch {
    return null;
  }
}

function readBooleanOverride(): boolean | null {
  const storage = getStorage();
  if (!storage) return null;
  const raw = storage.getItem(OPENAI_NATIVE_ENABLED_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
}

function readStage(): OpenAINativeRolloutStage {
  const storage = getStorage();
  if (!storage) return DEFAULT_STAGE;
  const raw = (storage.getItem(OPENAI_NATIVE_STAGE_KEY) || "").trim().toLowerCase();
  if (raw === "off" || raw === "canary10" || raw === "canary25" || raw === "canary50" || raw === "on") {
    return raw;
  }
  return DEFAULT_STAGE;
}

function getOrCreateBucket(): number | undefined {
  const storage = getStorage();
  if (!storage) return undefined;

  const raw = storage.getItem(OPENAI_NATIVE_BUCKET_KEY);
  if (raw) {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 99) {
      return parsed;
    }
  }

  const generated = Math.floor(Math.random() * 100);
  storage.setItem(OPENAI_NATIVE_BUCKET_KEY, String(generated));
  return generated;
}

export function getOpenAINativeRolloutTelemetry(): OpenAINativeRolloutTelemetry {
  const explicit = readBooleanOverride();
  const stage = readStage();

  if (explicit !== null) {
    return {
      enabled: explicit,
      stage,
      source: "explicit",
      bucket: getOrCreateBucket(),
    };
  }

  if (stage === "off") {
    return {
      enabled: false,
      stage,
      source: "stage",
      bucket: getOrCreateBucket(),
      rolloutPercent: 0,
    };
  }

  if (stage === "on") {
    return {
      enabled: true,
      stage,
      source: "stage",
      bucket: getOrCreateBucket(),
      rolloutPercent: 100,
    };
  }

  const percentMatch = stage.match(/^canary(\d{1,3})$/);
  const rolloutPercent = percentMatch ? Math.max(0, Math.min(Number.parseInt(percentMatch[1], 10), 100)) : 0;
  const bucket = getOrCreateBucket();
  const enabled = bucket !== undefined ? bucket < rolloutPercent : false;
  return {
    enabled,
    stage,
    source: "stage",
    bucket,
    rolloutPercent,
  };
}

export function isOpenAINativeEnabled(): boolean {
  return getOpenAINativeRolloutTelemetry().enabled;
}

export function recordOpenAINativeMetric(
  metric: string,
  details: Record<string, unknown> = {}
): void {
  const telemetry = getOpenAINativeRolloutTelemetry();
  const payload: Record<string, unknown> = {
    metric,
    stage: telemetry.stage,
    enabled: telemetry.enabled,
    source: telemetry.source,
    ...details,
  };
  if (telemetry.bucket !== undefined) {
    payload.bucket = telemetry.bucket;
  }
  if (telemetry.rolloutPercent !== undefined) {
    payload.rolloutPercent = telemetry.rolloutPercent;
  }
  console.log(`[OPENAI_NATIVE_METRIC] ${JSON.stringify(payload)}`);
}

